/**
 * MIRROR of src/services/workshop/contracts.ts (backend) — the response shapes of /api/v1/workshop/*.
 * Keep the two in step: a field added there is added here in the same change. Types only (+ the bucket lists).
 */
// ── Mirrored from src/services/workshop/orderState.ts (backend) ──
export type WsBucket = 'ACTION' | 'PRICING' | 'PREPARING' | 'ON_THE_WAY' | 'DELIVERED_UNPAID' | 'DONE' | 'LAPSED';
export const WS_BUCKETS: readonly WsBucket[] = ['ACTION', 'PRICING', 'PREPARING', 'ON_THE_WAY', 'DELIVERED_UNPAID', 'DONE', 'LAPSED'];
export const WS_ACTIVE_BUCKETS: readonly WsBucket[] = ['ACTION', 'PRICING', 'PREPARING', 'ON_THE_WAY', 'DELIVERED_UNPAID'];
export type WaitingCode =
  | 'APPROVE' | 'RECEIVE' | 'PAY_OVERDUE' | 'INVOICE' | 'PRICING' | 'PREPARING' | 'READY_PICKUP' | 'ON_DELIVERY'
  | 'WITH_DRIVER' | 'UNPAID' | 'RETURN_PENDING' | 'NOTHING_AVAILABLE' | 'DONE' | 'CANCELED' | 'LAPSED';
export interface WaitingOn {
  code: WaitingCode;
  /** Ready-to-show sentence, Arabic (Arabic-Indic digits). */
  ar: string;
  en: string;
  params?: { priced?: number; total?: number; count?: number; amount?: number; days?: number };
}
export type WaitingParty = 'WORKSHOP' | 'SUPPLIERS' | 'MIZE' | 'NOBODY';
export type ItemDelivery = 'PREPARING' | 'READY' | 'IN_TRANSIT' | 'DELIVERED';

export type Iso = string;
export type Ymd = string;
export type WorkshopKind = 'PRIVATE' | 'INDEPENDENT';
/** A sentence in both languages (server-built; Arabic uses Arabic-Indic digits). */
export interface Bilingual { ar: string; en: string }

export interface WsCar {
  make: string | null;
  model: string | null;
  year: number | null;
  plate: string | null;
}

export interface WsLapseInfo {
  /** When the automatic sweep cancelled it. */
  at: Iso | null;
  /** Riyadh days since. */
  daysAgo: number | null;
  /** ≤ 30 days → «إلغاء الانتهاء» (same order, same prices, back to `fromStatus`); else only «أعد الطلب». */
  restorable: boolean;
  /** The status before the lapse (order_status_history). */
  fromStatus: string | null;
}

/** The state-machine verdict every list / card / page shows (services/workshop/orderState.ts). */
export interface WsOrderStateView {
  bucket: WsBucket;
  waitingOn: WaitingOn;
  party: WaitingParty;
  /** «عُرض منذ X أيام» — null before the first price. */
  quotedDaysAgo: number | null;
  lapse: WsLapseInfo | null;
}

// ═══ GET /api/v1/workshop/pulse ══════════════════════════════════════════════════════════════
/** Polled every 45 s while the tab is visible and on focus; drives the nav badges. Cheap. */
export interface WsPulse {
  /** Orders in ACTION («بانتظارك») — the «الطلبات» badge. */
  action: number;
  /** Count per active bucket. */
  buckets: Record<WsBucket, number>;
  /** Unread order-chat messages; null when not computed yet. */
  unreadChats: number | null;
  /** INDEPENDENT: delivered orders unpaid > 30 days; PRIVATE: null. */
  overdue: number | null;
  /** New low ratings (≤ 3★, last 7 days, unacknowledged); null when not computed yet. */
  lowRatings: number | null;
  ts: Iso;
}

// ═══ GET /api/v1/workshop/home ═══════════════════════════════════════════════════════════════
export type WsQueueType =
  | 'APPROVE_QUOTE'   // priced items await the workshop's choice
  | 'RECEIVE_PARTS'   // delivered — confirm receipt
  | 'PAY_DUE'         // INDEPENDENT: delivered, unpaid past the grace days
  | 'INVOICE_NEEDED'  // PRIVATE: paid, no sale invoice yet
  | 'CASH_HANDOVER'   // PRIVATE: custody cash to hand over
  | 'RETURN_UPDATE'   // a return was approved / rejected in the last 48 h
  | 'LOW_RATING'      // a ≤ 3★ rating in the last 7 days, not acknowledged
  | 'PICKUP_READY'    // PRIVATE: parts ready at the supplier — the branch collects them (pickup list)
  | 'LAPSED_RESTORABLE'; // auto-lapsed ≤ 30 Riyadh days ago — «إلغاء الانتهاء»; orderId null = several (→ the «منتهية» list)

/** What the card's one primary button does — the screen maps it to an existing modal / route. */
export type WsQueueActionKind =
  | 'OPEN_ORDER' | 'CHOOSE_PARTS' | 'RECEIVE' | 'PAY' | 'INVOICE' | 'CUSTODY' | 'CALL_CUSTOMER'
  | 'PICKUP_LIST'  // the order page's supplier pickup list
  | 'RESTORE';     // POST /orders/:id/restore behind a confirm; orderId null → /orders?bucket=LAPSED

export interface WsQueueItem {
  /** Stable key (`${type}:${orderId|ref}`) — the phone's «لاحقاً» snooze is keyed on it. */
  key: string;
  type: WsQueueType;
  orderId: string | null;
  orderNumber: string | null;
  car: WsCar | null;
  customerName: string | null;
  /** One sentence, e.g. «كامري ٢٠١٩ · ٧ قطع مسعّرة — عُرض منذ ٣ أيام». */
  sentence: Bilingual;
  /** The money the card is about (SAR), when there is one. */
  amount: number | null;
  /** «عُرض منذ X أيام» for APPROVE_QUOTE; null otherwise. */
  quotedDaysAgo: number | null;
  /** Riyadh days the task has been waiting. */
  ageDays: number;
  severity: 'info' | 'warn' | 'bad';
  action: { kind: WsQueueActionKind; label: Bilingual };
  /** LOW_RATING only: the workshop's own customer's phone for «اتصل» (CALL_CUSTOMER). Absent on other cards. */
  customerPhone?: string | null;
}

export interface WsPipelineSegment {
  bucket: Exclude<WsBucket, 'DONE' | 'LAPSED'>;
  count: number;
  /** SAR: quoted max for ACTION/PRICING, confirmed total otherwise. */
  value: number;
  /** Orders in this bucket older than the branch's own 75th percentile age; null when not enough history. */
  aging: { olderThanHours: number; count: number } | null;
}

/** Ageing of outstanding money (INDEPENDENT): 0–7, 8–30, 31–60, over 60 days. */
export interface WsAgeingBucket { key: '0_7' | '8_30' | '31_60' | '60_plus'; amount: number; orders: number }

/** Money for an INDEPENDENT workshop: it owes Mize on account. */
export interface WsMoneyIndependent {
  kind: 'INDEPENDENT';
  /** Σ remaining on its delivered orders (orders side). */
  outstanding: number;
  /** Of which delivered more than 30 days ago. */
  overdue: number;
  ageing: WsAgeingBucket[];
  /** From the books client ledger (booksCredit) — null when the workshop has no limit / no books client. */
  credit: { limit: number; balance: number; headroom: number } | null;
  paidThisMonth: number;
}

/** Money for a PRIVATE (owned) branch: it collects from its own customers and holds cash for Mize. No credit meter. */
export interface WsMoneyPrivate {
  kind: 'PRIVATE';
  /** Σ remaining on delivered / received orders — still to collect from the branch's customers. */
  toCollect: number;
  collectedThisMonth: number;
  /** Cash custody (workshop_cash_custody) — null when the branch keeps none. */
  custody: { balance: number; oldestDays: number | null } | null;
}

export type WsHomeMoney = WsMoneyIndependent | WsMoneyPrivate;

/** `conversion` is a 0–1 ratio; `decisionHours` = the workshop's own median quote → first confirm. */
export type WsKpiKey = 'orders' | 'conversion' | 'decisionHours' | 'readyToDeliveredHours' | 'rating';

export interface WsKpi {
  key: WsKpiKey;
  /** This month so far. */
  value: number | null;
  /** The same span of last month. */
  prev: number | null;
  unit: 'count' | 'pct' | 'hours' | 'stars';
  /** true when a higher value is better (rating, conversion); false for durations. */
  higherIsBetter: boolean;
  /** Last 8 weeks, oldest first. */
  spark: number[];
  /** «متوسط الشبكة» (anonymous median, rolling 90 days) — or the fallback «مقارنة بفترتك السابقة». */
  benchmark: { kind: 'NETWORK'; p50: number; n: number } | { kind: 'PREVIOUS'; value: number | null } | null;
}

export interface WsBonusProgress {
  tier: string | null;
  /** 0–100 toward the next tier. */
  progressPct: number;
  /** SAR left to the next tier; null at the top tier. */
  toNext: number | null;
  daysLeft: number;
  /** SAR per remaining day to reach the next tier. */
  perDay: number | null;
  /** INDEPENDENT: anonymous rank only. PRIVATE: the same (names stay on the bonus page). */
  rank: { rank: number; of: number } | null;
}

export type WsActivityKind = 'PRICED' | 'CONFIRMED' | 'READY' | 'WITH_DRIVER' | 'DELIVERED' | 'RECEIVED' | 'PAID' | 'RATED' | 'RETURN';

export interface WsActivityItem {
  at: Iso;
  orderId: string;
  orderNumber: string;
  kind: WsActivityKind;
  text: Bilingual;
}

export interface WsHomeResponse {
  /** The greeting names the WORKSHOP (one shared login), never a person. */
  workshop: { name: string; type: WorkshopKind; brandKey: string | null };
  /** Riyadh today and its one-line summary («اليوم: ٤ طلبات جديدة · ٢ تسليم · ٣٬٤٠٠ ر.س محصّل»). */
  today: { date: Ymd; newOrders: number; delivered: number; collected: number };
  /** «بانتظارك الآن» — sorted by money at risk × age; at most 6, `total` for «عرض الكل». */
  queue: { items: WsQueueItem[]; total: number };
  pipeline: WsPipelineSegment[];
  money: WsHomeMoney;
  /** This month's mini insights; null while not built. */
  mini: { kpis: WsKpi[]; insight: (Bilingual & { href: string | null }) | null } | null;
  /** Null when the workshop is not enrolled in the sales bonus. */
  bonus: WsBonusProgress | null;
  /** The last 8 order events. */
  activity: WsActivityItem[];
  ts: Iso;
}

// ═══ GET /api/v1/workshop/search?q= ══════════════════════════════════════════════════════════
/** Plate (Latin→Arabic normalised), order number, customer name/phone, part name. At most 5 per group. */
export interface WsSearchOrder extends WsOrderStateView {
  id: string;
  orderNumber: string;
  car: WsCar;
  customerName: string | null;
  createdAt: Iso;
}
export interface WsSearchCar extends WsCar {
  orders: number;
  lastOrderId: string | null;
  lastOrderAt: Iso | null;
}
export interface WsSearchCustomer {
  /** customer_profiles.id when the customer has a profile. */
  id: string | null;
  name: string | null;
  phone: string | null;
  orders: number;
  lastOrderAt: Iso | null;
}
export interface WsSearchResponse {
  q: string;
  orders: WsSearchOrder[];
  cars: WsSearchCar[];
  customers: WsSearchCustomer[];
}

// ═══ GET /api/v1/workshop/orders ═════════════════════════════════════════════════════════════
/** `ALL_ACTIVE` = every bucket but DONE and LAPSED («الكل نشط»); `ALL` = everything. */
export type WsBucketFilter = WsBucket | 'ALL_ACTIVE' | 'ALL';

export interface WsOrdersQuery {
  bucket?: WsBucketFilter;
  /** Plate, order number, customer name / phone, part name — server-side. */
  q?: string;
  from?: Ymd;
  to?: Ymd;
  make?: string;
  receptionistId?: string;
  unpaid?: boolean;
  hasReturn?: boolean;
  /** Opaque, from the previous page's `next`. */
  cursor?: string;
  /** Default 30, max 60. */
  limit?: number;
}

export interface WsOrderListItem extends WsOrderStateView {
  id: string;
  orderNumber: string;
  createdAt: Iso;
  car: WsCar;
  customer: { name: string | null; phone: string | null };
  receptionist: { id: string; name: string } | null;
  /** The first two part names, then «+N». */
  parts: { first: string[]; more: number; total: number };
  /** Before confirmation: the quoted range (cheapest…dearest per item); after: the confirmed total. */
  value: { min: number | null; max: number | null; confirmed: number | null };
  paid: number;
  remaining: number;
  /** Order-chat messages the workshop has not read. */
  unreadChat: number;
  hasReturn: boolean;
}

export interface WsOrdersListResponse {
  items: WsOrderListItem[];
  /** Cursor for the next page; null at the end. */
  next: string | null;
  /** Count and SAR per bucket over the whole filter (not just this page). */
  summary: Record<WsBucket, { count: number; value: number }>;
  /** «هذا الشهر: ٤٢ طلب · ٦٨٬٢٠٠ ر.س» (Riyadh month). */
  month: { orders: number; value: number };
}

// ═══ GET /api/v1/workshop/orders/:id/summary ═════════════════════════════════════════════════
/** The 6-step track in workshop words: «طُلب» → «سُعّر» → «اخترت» → «في الطريق» → «وصل» → «دُفع». */
export type WsTrackKey = 'ORDERED' | 'PRICED' | 'CHOSEN' | 'ON_THE_WAY' | 'ARRIVED' | 'PAID';
export interface WsTrackStep { key: WsTrackKey; state: 'done' | 'current' | 'todo'; at: Iso | null }

/** What the page may offer — computed server-side from the same rules the existing endpoints enforce. Each maps to
 *  an EXISTING endpoint / modal (confirm, unconfirm, return, receive, pay, invoice, chat, add items …). */
export type WsOrderAction =
  | 'CONFIRM_ITEMS' | 'UNCONFIRM_ITEM' | 'REQUEST_RETURN' | 'CANCEL_RETURN'
  | 'RECEIVE' | 'UNRECEIVE' | 'PAY' | 'CASH_PAYMENT' | 'POS_CHARGE'
  | 'REQUEST_INVOICE' | 'ISSUE_CASH_INVOICE' | 'DOWNLOAD_INVOICE'
  | 'ADD_ITEMS' | 'EDIT_DETAILS' | 'DELETE_UNPRICED_ITEM' | 'ITEM_COMMENT' | 'RECEIVING_LOCATION'
  | 'CHAT' | 'ORDER_UPDATES' | 'PICKUP_LIST' | 'FEEDBACK_LINK' | 'COPY_EXCEL' | 'PRINT_QUOTE'
  | 'RESTORE_LAPSED' | 'REQUOTE';

export interface WsPricePill {
  /** Sale price per unit (SAR incl. VAT) — never a cost. */
  price: number;
  /** null = «غير محدد». */
  leadDays: number | null;
}

export interface WsOrderItemView {
  id: string;
  partName: string;
  quantity: number;
  batchNo: number | null;
  oem: WsPricePill | null;
  aftermarket: WsPricePill | null;
  wantsBothPrices: boolean;
  isUnavailable: boolean;
  isConfirmed: boolean;
  confirmedPriceType: 'OEM' | 'AFTERMARKET' | null;
  /** Confirmed unit price (SAR). */
  unitPrice: number | null;
  /** Priced, unconfirmed and awaiting the workshop's decision (orderState rule 2). */
  awaitingDecision: boolean;
  /** Confirmed items only. */
  delivery: ItemDelivery | null;
  /** With the driver: the driver's first name only. */
  driverName: string | null;
  receivedAt: Iso | null;
  return: { state: 'PENDING' | 'APPROVED' | 'REJECTED' | 'RETURNED'; requestedAt: Iso | null } | null;
  workshopComment: string | null;
  /** PRIVATE only (pickup): the supplier's name. INDEPENDENT: always null. */
  supplierName: string | null;
}

export type WsTimelineKind = 'STATUS' | 'PAYMENT' | 'RETURN' | 'RECEIVED' | 'UPDATE' | 'CUSTOMER';
/** `status` = the order status a STATUS event moved to (drives the icon: sent, priced, confirmed, ready, delivered …). */
export interface WsTimelineEvent { at: Iso; kind: WsTimelineKind; text: Bilingual; status?: string }

export interface WsOrderSummary extends WsOrderStateView {
  id: string;
  orderNumber: string;
  /** The raw order status — for the reused modals that still key on it. */
  status: string;
  createdAt: Iso;
  car: WsCar & { vin: string | null };
  customer: { name: string | null; phone: string | null; profileId: string | null };
  receptionist: { id: string; name: string } | null;
  receivingLocation: { id: string; name: string } | null;
  track: WsTrackStep[];
  /** «عادةً …» — median-based, never the delivery schedule; null when there is not enough history. */
  usually: Bilingual | null;
  counts: {
    total: number; priced: number; unpriced: number; unavailable: number; awaitingDecision: number;
    confirmed: number; preparing: number; ready: number; withDriver: number; received: number;
    returnsPending: number; returned: number;
  };
  money: {
    due: number;
    paid: number;
    remaining: number;
    lastPayment: { amount: number; method: string; at: Iso } | null;
  };
  /** Shown once a sale invoice exists. */
  invoice: { number: string | null; date: Iso | null; downloadable: boolean } | null;
  /** `requiresSignedReceivingSheet` workshops: invoicing waits for «سند التسليم». */
  signedNote: { required: boolean; uploadedAt: Iso | null };
  items: WsOrderItemView[];
  timeline: WsTimelineEvent[];
  allowedActions: WsOrderAction[];
}

// ═══ Lapsed orders («منتهية») ════════════════════════════════════════════════════════════════
// The list is GET /orders?bucket=LAPSED (each row carries `lapse`).

/** POST /api/v1/workshop/orders/:id/restore — «إلغاء الانتهاء»: the SAME order, its prices, back to `fromStatus`.
 *  Only an auto-lapsed order, lapsed ≤ 30 Riyadh days ago. */
export interface WsRestoreLapsedResponse {
  ok: true;
  orderId: string;
  orderNumber: string;
  status: string;
}

/** POST /api/v1/workshop/orders/:id/requote — «أعد الطلب»: the items cloned into a NEW order for fresh prices. The
 *  one-open-order-per-car rule (workshops.allow_same_car_orders) applies. */
export interface WsRequoteResponse {
  ok: true;
  newOrderId: string;
  newOrderNumber: string;
  items: number;
}

/** Error codes the two lapsed actions return (body: { error: { code, message } }, message in Arabic). */
export type WsLapsedErrorCode =
  | 'NOT_LAPSED'          // not cancelled by the automatic lapse
  | 'LAPSE_TOO_OLD'       // restore: > 30 days → requote instead
  | 'LAPSE_TOO_RECENT'    // requote: ≤ 30 days → restore instead
  | 'OPEN_ORDER_FOR_CAR'  // the car has an undelivered order and the switch forbids a second
  | 'NOT_FOUND';
